import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styles from '../styles/Fundraiser.module.css';
import runningImage from '../assets/running.jpg';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const blockVariants = {
  hidden: { y: 20, opacity: 0, scale: 0.95 },
  visible: {
    y: 0, opacity: 1, scale: 1,
    transition: { type: "spring", stiffness: 120 }
  }
};

function Fundraiser() {
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, organization, email }),
      });

      if (!response.ok) {
        throw new Error('Something went wrong. Please try again.');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      className={styles.container}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className={styles.shapeOne}></div>
      <div className={styles.shapeTwo}></div>

      {/* Hero */}
      <section className={styles.hero}>
        <motion.div className={styles.heroContent} variants={blockVariants}>
          <h1 className={styles.heroTitle}>Taco Bell 50K</h1>
          <p className={styles.heroSubtitle}>
            I'm running 31 miles with 10 mandatory Taco Bell stops and raising money for <a href="https://www.poynter.org/mediawise/" target="_blank" rel="noopener noreferrer">MediaWise</a> and the <a href="https://www.poynter.org/" target="_blank" rel="noopener noreferrer">Poynter Institute</a>. Yes, you have to eat at 9 of the 10 stops. Yes, this is completely stupid. If you'd like to support the cause, drop your info below and I'll be in touch with details once I figure out what that looks like.
          </p>
        </motion.div>

        <motion.div className={styles.imageContainer} variants={blockVariants}>
          <img src={runningImage} alt="Alex running" className={styles.runningImage} />
        </motion.div>
      </section>

      {/* About the race */}
      <section className={styles.about}>
        <motion.div className={styles.aboutBox} variants={blockVariants}>
          <h2 className={styles.headerTitle}>The Race</h2>
          <p>
            The Taco Bell 50K is a 31-mile ultramarathon with 10 Taco Bell stops along the course. You must eat a menu item at 9 of the 10 stops. By stop 4, you need to have eaten a Chalupa Supreme or Crunchwrap Supreme. By stop 8, a Burrito Supreme or Nachos Bell Grande. Drinks don't count as food.  Finish under 11 hours (very important detail for me!). No Pepto. No Alka Seltzer.
          </p>
          <p style={{ marginTop: 'var(--space-sm)' }}>
            There are also two optional bonus challenges. If a supporter wants to fund it, I'll take on the <strong>Diablo Challenge</strong> (lather all items with Diablo sauce and do a Diablo shooter at the end) and/or the <strong>Baja Blast Challenge</strong> (drink 2 liters of Baja Blast during the run without vomiting). Congratulations?
          </p>
        </motion.div>
      </section>

      {/* What supporters get */}
      <section className={styles.about}>
        <motion.div className={styles.aboutBox} variants={blockVariants}>
          <h2 className={styles.headerTitle} style={{ transform: 'rotate(1deg)' }}>What You Get</h2>
          <p>
            All proceeds go to <a href="https://www.poynter.org/mediawise/" target="_blank" rel="noopener noreferrer">MediaWise</a> and the <a href="https://www.poynter.org/" target="_blank" rel="noopener noreferrer">Poynter Institute</a> to fund programs that teach people how to fight misinformation. In return for your support here are some of what I may offer:
          </p>
          <ul className={styles.perksList}>
            <li>I'll wear your logo during the race</li>
            <li>You'll get a shoutout in the recap video</li>
            <li>Youll get the satisfaction of knowing you funded media literacy while a man humiliated himself on video</li>
          </ul>
        </motion.div>
      </section>

      {/* Sign up form */}
      <section className={styles.formSection}>
        <motion.div className={styles.formBox} variants={blockVariants}>
          <h2 className={styles.headerTitle} style={{ transform: 'rotate(1deg)' }}>I'm In</h2>
          {submitted ? (
            <div className={styles.thankYou}>
              <p>Thanks, {name}! I'll be in touch soon with more details.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label htmlFor="name" className={styles.label}>Name</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className={styles.input}
                  placeholder="Your name"
                />
              </div>
              <div className={styles.fieldGroup}>
                <label htmlFor="organization" className={styles.label}>Organization</label>
                <input
                  id="organization"
                  type="text"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  className={styles.input}
                  placeholder="Your organization (optional)"
                />
              </div>
              <div className={styles.fieldGroup}>
                <label htmlFor="email" className={styles.label}>Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={styles.input}
                  placeholder="your@email.com"
                />
              </div>
              {error && <p className={styles.error}>{error}</p>}
              <button type="submit" className={styles.submitButton} disabled={submitting}>
                {submitting ? 'Sending...' : 'Count Me In'}
              </button>
            </form>
          )}
        </motion.div>
      </section>
    </motion.div>
  );
}

export default Fundraiser;
